/**
 * Filing History Page
 *
 * Main page for viewing and managing filing history.
 *
 * Session 3.2: Filing History Frontend
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { FilingList } from '@/components/filing/FilingList';
import { FilingDetailModal } from '@/components/filing/FilingDetailModal';
import {
  useFilings,
  useFilingYears,
  useFilingDetail,
  useDownloadC10,
  useMarkAsFiled,
  useUpdateNotes,
} from '@/hooks/useFilings';
import type { FilingSummary } from '@/types/filing';

/**
 * Filing History Page component.
 *
 * Features:
 * - Year filter dropdown
 * - Paginated filing list
 * - Detail modal on row click
 * - Download C10 files
 * - Mark filings as filed
 */
export function FilingHistoryPage() {
  // Filter state
  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    undefined
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);

  // Modal state
  const [selectedFilingId, setSelectedFilingId] = useState<string | null>(null);

  // Queries
  const {
    data: filingsData,
    isLoading: isLoadingFilings,
    error: filingsError,
  } = useFilings({
    year: selectedYear,
    page: currentPage,
    per_page: perPage,
  });

  const { data: years } = useFilingYears();

  const { data: filingDetail, isLoading: isLoadingDetail } =
    useFilingDetail(selectedFilingId);

  // Mutations
  const { mutate: download, isPending: isDownloading } = useDownloadC10();
  const { mutate: markFiled, isPending: isMarking } = useMarkAsFiled();
  const { mutate: updateNotes, isPending: isUpdatingNotes } = useUpdateNotes();

  // Handlers
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedYear(value ? parseInt(value, 10) : undefined);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleRowClick = (filing: FilingSummary) => {
    setSelectedFilingId(filing.id);
  };

  const handleDownloadFromList = (filing: FilingSummary) => {
    const filename = `C10_${filing.period_display.replace(' ', '_')}.txt`;
    download(
      { id: filing.id, filename },
      {
        onSuccess: () => {
          toast.success('C10 file downloaded');
        },
        onError: () => {
          toast.error('Failed to download C10 file');
        },
      }
    );
  };

  const handleDownloadFromModal = () => {
    if (!filingDetail) return;
    const filename = `C10_${filingDetail.period_display.replace(' ', '_')}.txt`;
    download(
      { id: filingDetail.id, filename },
      {
        onSuccess: () => {
          toast.success('C10 file downloaded');
        },
        onError: () => {
          toast.error('Failed to download C10 file');
        },
      }
    );
  };

  const handleMarkAsFiled = () => {
    if (!filingDetail) return;
    markFiled(
      { id: filingDetail.id },
      {
        onSuccess: () => {
          toast.success('Filing marked as submitted');
        },
        onError: () => {
          toast.error('Failed to update filing status');
        },
      }
    );
  };

  const handleUpdateNotes = (notes: string) => {
    if (!filingDetail) return;
    updateNotes(
      { id: filingDetail.id, notes },
      {
        onSuccess: () => {
          toast.success('Notes updated');
        },
        onError: () => {
          toast.error('Failed to update notes');
        },
      }
    );
  };

  const handleCloseModal = () => {
    setSelectedFilingId(null);
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    if (filingsData?.pagination) {
      setCurrentPage(prev =>
        Math.min(filingsData.pagination.total_pages, prev + 1)
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Filing History</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your C10 filing submissions
          </p>
        </div>

        {/* Year Filter */}
        <div className="flex items-center space-x-2">
          <label
            htmlFor="year-filter"
            className="text-sm font-medium text-gray-700"
          >
            Year:
          </label>
          <select
            id="year-filter"
            value={selectedYear ?? ''}
            onChange={handleYearChange}
            className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">All Years</option>
            {years?.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error State */}
      {filingsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p className="font-medium">Error loading filings</p>
          <p className="text-sm">
            {filingsError instanceof Error
              ? filingsError.message
              : 'An unexpected error occurred'}
          </p>
        </div>
      )}

      {/* Filing List */}
      <FilingList
        filings={filingsData?.filings ?? []}
        isLoading={isLoadingFilings}
        onRowClick={handleRowClick}
        onDownload={handleDownloadFromList}
      />

      {/* Pagination */}
      {filingsData?.pagination && filingsData.pagination.total_pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg">
          <div className="text-sm text-gray-500">
            Showing{' '}
            <span className="font-medium">
              {(currentPage - 1) * perPage + 1}
            </span>{' '}
            to{' '}
            <span className="font-medium">
              {Math.min(currentPage * perPage, filingsData.pagination.total)}
            </span>{' '}
            of{' '}
            <span className="font-medium">{filingsData.pagination.total}</span>{' '}
            results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-700">
              Page {currentPage} of {filingsData.pagination.total_pages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === filingsData.pagination.total_pages}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedFilingId && (
        <FilingDetailModal
          filing={filingDetail}
          isLoading={isLoadingDetail}
          onClose={handleCloseModal}
          onDownload={handleDownloadFromModal}
          onMarkAsFiled={handleMarkAsFiled}
          onUpdateNotes={handleUpdateNotes}
          isDownloading={isDownloading}
          isMarking={isMarking}
          isUpdatingNotes={isUpdatingNotes}
        />
      )}
    </div>
  );
}

export default FilingHistoryPage;
